import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppDispatch } from "../../../core/data/redux/store";
import { createLeaveType, updateLeaveType, fetchAllLeaveTypes, deleteLeaveType } from "../../../core/data/redux/attendance/leaveTypes/LeaveTypeActions";
import { formatDate } from "../../../core/utils/configFormatters";
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from "../../../core/common/collapse-header/collapse-header";
import DeleteModal1 from "../../../core/modals/deleteModal1";
import { all_routes } from "../../router/all_routes";
import { exportToCSV, exportToExcel, exportToPDF, } from "../../../core/common/GlobalFunctions/exportTableData";
import { filterData } from "../../../core/common/GlobalFunctions/tableFilters";
import { Drawer, message } from "antd";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { decryptData } from "../../../core/utils/encryptUtils";
import { render } from "@fullcalendar/core/preact";

const LeaveTypes = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [selectedSort, setSelectedSort] = useState<any>("");
  const [tableKey, setTableKey] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
  const [modalType, setModalType] = useState<"edit" | "create" | "">("");
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const { user } = useSelector((state: any) => state.auth);
  const { leaveTypes, loadingStates } = useSelector((state: any) => state.attendance.leaveTypes);
  const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

  const encryptedPermissions = localStorage.getItem('currentPermissions');
  const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];

  const canAdd =
    permissions.includes("Add") || permissions.includes("AllowAll");
  const canView =
    permissions.includes("View") || permissions.includes("AllowAll");
  const canEdit =
    permissions.includes("Edit") || permissions.includes("AllowAll");
  const canDelete =
    permissions.includes("Delete") || permissions.includes("AllowAll");
  const canExport =
    permissions.includes("Export") || permissions.includes("AllowAll");


  const leaveTypeId = selectedRowData?.Id;

  const [formData, setFormData] = useState({
    Name: "",
    Description: "",
    Status: "",
    Credit: "",
    Frequency: "",
    createdBy: "",
    modifiedBy: "",
    Gender: ""
  });

  const [errors, setErrors] = useState<any>({});

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    dispatch(fetchAllLeaveTypes());
  }, [dispatch]);

  const showModal = (type: "edit" | "create") => {
    setModalType(type);
    setIsModalVisible(true);
    setErrors({});
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setModalType("");
    // resetFields();
    setErrors({});
  };

  const handleEditDepartment = (record: any) => {
    setSelectedRowData(record);

    setFormData({
      Name: record.Name || "",
      Description: record.Description || null,
      Status: record.Status || "",
      Credit: record.Credit || "",
      Frequency: record.Frequency || "",
      createdBy: record.CreatedBy || "",
      modifiedBy: record.ModifiedBy || "",
      Gender: record.Gender || "",
    });
  };

  const validateForm = () => {
    let formIsValid = true;

    let newErrors: any = {
      Name: "",
      Description: "",
      Status: "",
      Credit: "",
      Frequency: "",
      Gender: "",
    };

    if (!formData.Name.trim()) {
      formIsValid = false;
      newErrors.Name = "Name is required.";
    }

    if (!formData.Gender.trim()) {
      formIsValid = false;
      newErrors.Gender = "Gender is required.";
    }


    if (!formData.Status.trim()) {
      formIsValid = false;
      newErrors.Status = "Status is required.";
    }

    if (!formData.Credit) {
      formIsValid = false;
      newErrors.Credit = "Credit is required.";
    }


    if (!formData.Frequency.trim()) {
      formIsValid = false;
      newErrors.Frequency = "Frequency is required.";
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    let updatedFormData = { ...formData };
    const userId = user.UserId;

    if (modalType === "create") {
      updatedFormData.createdBy = userId;
    } else if (modalType === "edit") {
      updatedFormData.modifiedBy = userId;
    }

    let result;
    if (modalType === "create") {
      result = await dispatch(createLeaveType(updatedFormData));
    } else if (modalType === "edit" && leaveTypeId) {
      result = await dispatch(
        updateLeaveType({ leaveTypeId, data: updatedFormData })
      );
    }

    if (result?.meta?.requestStatus === "fulfilled") {
      message.success(
        `Leave Type ${modalType === "create" ? "created" : "updated"
        } successfully`
      );
      //   resetFields();
      await dispatch(fetchAllLeaveTypes());
      setTableKey((prevKey) => prevKey + 1);
    } else {
      message.warning(result?.payload?.error);
    }
    handleCancel();
  };

  const handleDeleteLeaveType = async (leaveTypeId: number) => {
    try {
      const result = await dispatch(deleteLeaveType(leaveTypeId));
      if (result?.meta?.requestStatus === "fulfilled") {
        message.success(result.payload.message);
        setIsDeleteModalVisible(false);
        await dispatch(fetchAllLeaveTypes());
        setTableKey((prevKey) => prevKey + 1);
      } else {
        message.warning(result?.payload?.error);
      }
    } catch (error) {
      message.error("Something went wrong");
    }
  };

  const data = leaveTypes || [];

  const exportLeaveTypes = data.map((item: any) => ({
    Name: item.Name || "N/A",
    Description: item.Description || "N/A",
    Frequency: item.Frequency || "N/A",
    ProcessDate: item.ProcessDate ? new Date(item.ProcessDate).toLocaleDateString("en-IN") : "N/A",
    Status: item.Status || "Inactive",
    CreatedAt: item.CreatedAt ? new Date(item.CreatedAt).toLocaleDateString("en-IN") : "N/A",
    CreatedBy: item.CreatedBy || "N/A",
    ModifiedAt: item.ModifiedAt ? new Date(item.ModifiedAt).toLocaleDateString("en-IN") : "N/A",
    ModifiedBy: item.ModifiedBy || "N/A",

  }));


  const filteredData = useMemo(() => {
    const filters =
      selectedStatus && selectedStatus !== "All"
        ? { Status: selectedStatus }
        : {};
    return filterData(
      data || [],
      filters,
      "CreatedAt",
      "CreatedAt",
      selectedSort
    );
  }, [data, selectedStatus, selectedSort, leaveTypes]);

  useEffect(() => {
    setTableKey((prevKey) => prevKey + 1);
  }, [selectedStatus, selectedSort, leaveTypes]);

  const config = useAppConfig();
  if (!config) return null;

  const columns = [
    {
      title: "Leave Type",
      dataIndex: "Name",
      sorter: (a: any, b: any) => a.Name.length - b.Name.length,
    },
    {
      title: "Created Date",
      dataIndex: "CreatedAt",
      render: (text: string) => formatDate(text, config),
      sorter: (a: any, b: any) => a.CreatedAt.length - b.CreatedAt.length,
    },
    {
      title: "Description",
      dataIndex: "Description",
      render: (text: string) => text || "N/A",
      sorter: (a: any, b: any) => a.Description.length - b.Description.length,
    },
    {
      title: "Action",
      dataIndex: "actions",
      render: (text: string, record: any) => (
        <div className="action-icon d-inline-flex">
          <button
            className="me-2"
            onClick={() => {
              showModal("edit");
              handleEditDepartment(record);
            }} data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Edit"
          >
            <i className="ti ti-edit" />
          </button>
          <button
            onClick={() => {
              setSelectedRowData(record);
              setIsDeleteModalVisible(true);
            }}
            className="me-2"
            disabled={record.Status === "inactive"}
            style={{
              cursor: record.Status === "inactive" ? "not-allowed" : "pointer",
            }} data-bs-toggle="tooltip"
            data-bs-placement="top"
            title="Delete"
          >
            <i className="ti ti-trash" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          {/* Breadcrumb */}
          <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
            <div className="my-auto mb-2">
              <h2 className="mb-1">Leave Types</h2>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={all_routes.adminDashboard}>
                      <i className="ti ti-smart-home" />
                    </Link>
                  </li>
                  <li className="breadcrumb-item">Attendance</li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Leave Types
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
              {canExport &&
                <div className="me-2 mb-2">
                  <div className="dropdown">
                    <Link
                      to="#"
                      className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                      data-bs-toggle="dropdown"
                    >
                      <i className="ti ti-file-export me-1" />
                      Export
                    </Link>
                    <ul className="dropdown-menu  dropdown-menu-end p-3">
                      <li>
                        <button
                          onClick={() => exportToCSV(exportLeaveTypes, "leavetype_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-pdf me-1" />
                          Export as CSV
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToExcel(exportLeaveTypes, "leavetype_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as Excel{" "}
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => exportToPDF(exportLeaveTypes, "leavetype_data")}
                          className="dropdown-item rounded-1"
                        >
                          <i className="ti ti-file-type-xls me-1" />
                          Export as PDF{" "}
                        </button>
                      </li>
                    </ul>
                  </div>
                </div>
              }

              {canAdd &&
                <div className="mb-2">
                  <button
                    onClick={() => {
                      showModal("create");
                      setFormData({
                        Name: "",
                        Description: "",
                        Status: "",
                        Credit: "",
                        Frequency: "",
                        createdBy: "",
                        modifiedBy: "",
                        Gender: ""
                      });
                    }}
                    className="btn btn-primary d-flex align-items-center"
                  >
                    Add Leave Type
                  </button>
                </div>
              }
              <div className="head-icons ms-2">
                <CollapseHeader />
              </div>
            </div>
          </div>
          {/* /Breadcrumb */}

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Leave Type List</h5>
              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                <div className="dropdown me-3">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedStatus
                      ? selectedStatus.charAt(0).toUpperCase() +
                      selectedStatus.slice(1)
                      : "Select Status"}
                  </Link>
                  <ul className="dropdown-menu  dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("All")}
                        className="dropdown-item rounded-1"
                      >
                        All
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("active")}
                        className="dropdown-item rounded-1"
                      >
                        Active
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedStatus("inactive")}
                        className="dropdown-item rounded-1"
                      >
                        Inactive
                      </Link>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <Link
                    to="#"
                    className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center"
                    data-bs-toggle="dropdown"
                  >
                    {selectedSort
                      ? `Sort By : ${selectedSort}`
                      : "Sort By : Last 7 Days"}
                  </Link>
                  <ul className="dropdown-menu dropdown-menu-end p-3">
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Recently Added")}
                        className="dropdown-item rounded-1"
                      >
                        Recently Added
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Ascending")}
                        className="dropdown-item rounded-1"
                      >
                        Ascending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Descending")}
                        className="dropdown-item rounded-1"
                      >
                        Descending
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last Month")}
                        className="dropdown-item rounded-1"
                      >
                        Last Month
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="#"
                        onClick={() => setSelectedSort("Last 7 Days")}
                        className="dropdown-item rounded-1"
                      >
                        Last 7 Days
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <Table
                key={tableKey}
                dataSource={filteredData}
                columns={columns}
                Selection={true}
              />
            </div>
          </div>
        </div>

        <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
          <p className="mb-0">2024 - 2025© hrmtree.</p>
          <p>
            Developed By{" "}
            <Link to="#" className="text-primary">
              Leadchain Systems
            </Link>
          </p>
        </div>
      </div>

      {/* Modal for Add/Edit Leave Type */}
      <Drawer
        title={modalType === "edit" ? "Edit Leave Type" : "Add Leave Type"}
        placement="right"
        width={400}
        onClose={handleCancel}
        visible={isModalVisible}
        style={{ marginTop: 50, paddingBottom: 50 }}
      >
        <form action="roles-permissions.html">
          <div className="modal-body pb-0">
            <div className="row">
              {/* Leave Type Name */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Leave Type Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    name="Name"
                    className="form-control"
                    value={formData.Name}
                    onChange={(e) => {
                      const onlyLetters = e.target.value;
                      if (/^[a-zA-Z\s]*$/.test(onlyLetters)) {
                        setFormData((prev) => ({
                          ...prev,
                          Name: onlyLetters,
                        }));
                      }
                    }}
                  />

                  {errors.Name && (
                    <div style={{ color: "red" }}>{errors.Name}</div>
                  )}
                </div>
              </div>


              {/* Gender */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Gender <span className="text-danger">*</span>
                  </label>
                  <div className="d-flex gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="Gender"
                        id="male"
                        value="Male"
                        checked={formData.Gender === "Male"}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="male">
                        Male
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="Gender"
                        id="female"
                        value="Female"
                        checked={formData.Gender === "Female"}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="female">
                        Female
                      </label>
                    </div>

                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="Gender"
                        id="both"
                        value="Both"
                        checked={formData.Gender === "Both"}
                        onChange={handleChange}
                      />
                      <label className="form-check-label" htmlFor="both">
                        Both
                      </label>
                    </div>
                  </div>
                  {errors.Gender && (
                    <div style={{ color: "red" }}>{errors.Gender}</div>
                  )}
                </div>
              </div>
              {/* Credit */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Credit <span className="text-danger">*</span>
                  </label>
                  <select
                    name="Credit"
                    className="form-control"
                    value={formData.Credit}
                    onChange={handleChange}
                  >
                    <option value="">Select Credit</option>
                    <option value="Start">Start</option>
                    <option value="End">End</option>
                  </select>
                  {errors.Credit && (
                    <div style={{ color: "red" }}>{errors.Credit}</div>
                  )}
                </div>
              </div>

              {/* Frequency */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Frequency <span className="text-danger">*</span>
                  </label>
                  <select
                    name="Frequency"
                    className="form-control"
                    value={formData.Frequency}
                    onChange={handleChange}
                  >
                    <option value="">Select Frequency</option>
                    <option value="Monthly">Monthly</option>
                    <option value="Quarterly">Quarterly</option>
                    <option value="Yearly">Yearly</option>
                    <option value="On Request">On Request</option>
                  </select>
                  {errors.Frequency && (
                    <div style={{ color: "red" }}>{errors.Frequency}</div>
                  )}
                </div>
              </div>

              {/* Status */}
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Status <span className="text-danger">*</span>
                  </label>
                  <select
                    name="Status"
                    className="form-control"
                    value={formData.Status}
                    onChange={handleChange}
                  >
                    <option value="">Select Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.Status && (
                    <div style={{ color: "red" }}>{errors.Status}</div>
                  )}
                </div>
              </div>
              <div className="col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Description
                  </label>
                  <input
                    type="text"
                    name="Description"
                    className="form-control"
                    value={formData.Description || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-light me-2"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => handleSubmit()} disabled={isLoading}
            >{isLoading && <span className="spinner-border spinner-border-sm me-2" />}
              {modalType === "edit" ? "Update Leave Type" : "Add Leave Type"}
            </button>
          </div>
        </form>
      </Drawer>

      {/* Delete Modal */}
      <DeleteModal1
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onConfirm={() =>
          selectedRowData?.Id && handleDeleteLeaveType(selectedRowData.Id)
        }
      />
    </>
  );
};

export default LeaveTypes;
